// ═══════════════════════════════════════════════════════════════
// MY DAY SIDEBAR
// Path: src/components/dashboard/MyDaySidebar.jsx
//
// Left-side sticky sidebar on DashboardView answering "what
// do I need to do today?" in a glanceable format.
//
// Three sections (user-configurable via gear menu):
//   1. Tasks Due        — director_tasks due today or overdue
//   2. Overdue Touches  — my portfolio donors 30+ days without contact
//   3. Opportunity Gap  — my portfolio donors by capacity opportunity
//
// Each section shows top 3 items + a "See all ›" deep link into
// the appropriate full view.
//
// Collapsed state: 40px strip with count badges, click any badge
// to re-expand.
//
// Preferences persisted to profiles.my_day_sections (order + visibility)
// and profiles.my_day_collapsed (collapse state).
// ═══════════════════════════════════════════════════════════════

import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  ClipboardCheck,
  AlertTriangle,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Settings as Gear,
  ArrowUp,
  ArrowDown,
  ArrowRight,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { supabase } from "../../lib/supabase";
import { debugLog } from "../../utils/debugLog";
import {
  computeFiveYearGiving,
  resolveEffectiveBand,
  computeOpportunityGap,
} from "../../utils/capacityScoring";

// ── Section Registry ─────────────────────────────────────────
const SECTION_REGISTRY = [
  {
    id: "tasks",
    label: "Tasks Due",
    icon: ClipboardCheck,
    color: "#f59e0b",
    description: "Due today and overdue",
  },
  {
    id: "overdue",
    label: "Overdue Touches",
    icon: AlertTriangle,
    color: "#ef4444",
    description: "30+ days without contact",
  },
  {
    id: "opportunity",
    label: "Opportunity Gap",
    icon: TrendingUp,
    color: "#10b981",
    description: "Capacity upside in your portfolio",
  },
];

const DEFAULT_SECTIONS = ["tasks", "overdue", "opportunity"];

const EXPANDED_WIDTH = 320;
const COLLAPSED_WIDTH = 44;
const TOP_N = 3;

const getSection = (id) => SECTION_REGISTRY.find((s) => s.id === id);

const fmtMoney = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
};

const donorDisplayName = (member) => {
  if (!member) return "Unknown";
  if (member.lastName) {
    return `${member.firstName || ""} ${member.lastName}`.trim();
  }
  return member.organization || member.firstName || "Unknown";
};

const daysBetween = (dateStr) => {
  if (!dateStr) return null;
  const then = new Date(dateStr);
  if (isNaN(then.getTime())) return null;
  return Math.floor((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24));
};

// ── Data selectors ───────────────────────────────────────────

const useMyTasks = () => {
  const { openTasks, userProfile } = useApp();
  return useMemo(() => {
    const myName = userProfile?.full_name || "";
    const todayStr = new Date().toISOString().split("T")[0];
    const mine = openTasks.filter(
      (t) => !myName || !t.directorName || t.directorName === myName
    );
    const due = mine.filter((t) => t.dueDate && t.dueDate <= todayStr);
    due.sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
    return due;
  }, [openTasks, userProfile]);
};

const useMyOverdue = () => {
  const { members, lastContactByMember, userProfile } = useApp();
  return useMemo(() => {
    const myName = userProfile?.full_name || "";
    if (!myName) return [];
    const thirty = new Date();
    thirty.setDate(thirty.getDate() - 30);
    const cutoffStr = thirty.toISOString().slice(0, 10);
    const candidates = members
      .filter((m) => m.assignedDirector === myName)
      .filter((m) => (m.status || "active") === "active")
      .map((m) => ({
        member: m,
        lastContact: lastContactByMember?.[m.id] || null,
      }))
      .filter(({ lastContact }) => !lastContact || lastContact < cutoffStr);
    candidates.sort((a, b) => {
      // Never-contacted first (null), then oldest date first
      if (!a.lastContact && b.lastContact) return -1;
      if (a.lastContact && !b.lastContact) return 1;
      if (!a.lastContact && !b.lastContact) return 0;
      return a.lastContact.localeCompare(b.lastContact);
    });
    return candidates;
  }, [members, lastContactByMember, userProfile]);
};

const useMyOpportunity = () => {
  const { members, allGifts, allCapacity, userProfile } = useApp();
  return useMemo(() => {
    const myName = userProfile?.full_name || "";
    if (!myName) return [];
    // Index gifts by member id
    const giftsByMember = {};
    (allGifts || []).forEach((g) => {
      const mid = g.member_id || g.memberId;
      if (!mid) return;
      if (!giftsByMember[mid]) giftsByMember[mid] = [];
      giftsByMember[mid].push(g);
    });
    const rows = members
      .filter((m) => m.assignedDirector === myName)
      .map((m) => {
        const memberGifts = giftsByMember[m.id] || [];
        const capacity = (allCapacity && allCapacity[m.id]) || null;
        const fiveYear = computeFiveYearGiving(memberGifts);
        const effective = resolveEffectiveBand(capacity, fiveYear);
        const gap = computeOpportunityGap(effective.bandId, fiveYear);
        return { member: m, gap, fiveYear, bandSource: effective.source };
      })
      .filter((r) => r.gap != null && r.gap > 0);
    rows.sort((a, b) => b.gap - a.gap);
    return rows;
  }, [members, allGifts, allCapacity, userProfile]);
};

// ── Row renderers ────────────────────────────────────────────

const EmptyRow = ({ palette, text }) => (
  <div
    style={{
      fontSize: FONT_SIZES.xs,
      color: palette.text3,
      fontFamily: FONT_FAMILY,
      fontStyle: "italic",
      padding: "6px 2px",
    }}
  >
    {text}
  </div>
);

const DeepLinkRow = ({ palette, count, label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      marginTop: 4,
      background: "none",
      border: "none",
      padding: "4px 2px",
      fontSize: FONT_SIZES.xs,
      fontWeight: 600,
      color: palette.accent1 || palette.text2,
      fontFamily: FONT_FAMILY,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 4,
      textAlign: "left",
    }}
  >
    See all {count} {label} <ArrowRight size={ICON_SIZES.xs} />
  </button>
);

const ClickableDonorRow = ({ palette, title, subtitle, onClick, subtitleColor }) => (
  <button
    onClick={onClick}
    style={{
      background: "none",
      border: "none",
      width: "100%",
      textAlign: "left",
      padding: "6px 2px",
      cursor: "pointer",
      fontFamily: FONT_FAMILY,
      borderBottom: `1px solid ${palette.cardBorder}`,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <span
      style={{
        fontSize: FONT_SIZES.sm,
        fontWeight: 600,
        color: palette.text1,
      }}
    >
      {title}
    </span>
    <span
      style={{
        fontSize: "0.7rem",
        color: subtitleColor || palette.text3,
      }}
    >
      {subtitle}
    </span>
  </button>
);

// ── Section body components ──────────────────────────────────

const TasksSectionBody = ({ palette, navigate }) => {
  const tasks = useMyTasks();
  const { members } = useApp();
  const todayStr = new Date().toISOString().split("T")[0];

  if (tasks.length === 0) {
    return <EmptyRow palette={palette} text="No tasks due today or overdue." />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {tasks.slice(0, TOP_N).map((task) => {
        const daysOverdue = daysBetween(task.dueDate);
        const overdueLabel =
          task.dueDate < todayStr
            ? `${daysOverdue} day${daysOverdue === 1 ? "" : "s"} overdue`
            : "Due today";
        const subColor = task.dueDate < todayStr ? "#ef4444" : "#f59e0b";
        const linkedMember = task.memberId
          ? members.find((m) => m.id === task.memberId)
          : null;
        return (
          <ClickableDonorRow
            key={task.id}
            palette={palette}
            title={task.title || "Untitled task"}
            subtitle={overdueLabel}
            subtitleColor={subColor}
            onClick={() => {
              if (linkedMember) {
                navigate("memberDetail", linkedMember);
              } else {
                navigate("tasks");
              }
            }}
          />
        );
      })}
      {tasks.length > TOP_N && (
        <DeepLinkRow
          palette={palette}
          count={tasks.length}
          label="tasks"
          onClick={() => navigate("tasks")}
        />
      )}
    </div>
  );
};

const OverdueSectionBody = ({ palette, navigate }) => {
  const overdue = useMyOverdue();

  if (overdue.length === 0) {
    return (
      <EmptyRow
        palette={palette}
        text="Nobody in your portfolio is overdue. Good work."
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {overdue.slice(0, TOP_N).map(({ member, lastContact }) => {
        const days = daysBetween(lastContact);
        const subtitle = lastContact
          ? `${days} days since last contact`
          : "No contact on record";
        return (
          <ClickableDonorRow
            key={member.id}
            palette={palette}
            title={donorDisplayName(member)}
            subtitle={subtitle}
            subtitleColor="#ef4444"
            onClick={() => navigate("memberDetail", member)}
          />
        );
      })}
      {overdue.length > TOP_N && (
        <DeepLinkRow
          palette={palette}
          count={overdue.length}
          label="donors"
          onClick={() => navigate("cultivation")}
        />
      )}
    </div>
  );
};

const OpportunitySectionBody = ({ palette, navigate }) => {
  const opportunity = useMyOpportunity();

  if (opportunity.length === 0) {
    return (
      <EmptyRow
        palette={palette}
        text="No opportunity gap surfaced in your portfolio yet."
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {opportunity.slice(0, TOP_N).map(({ member, gap, bandSource }) => {
        const suggestedHint = bandSource === "suggested" ? " (suggested)" : "";
        return (
          <ClickableDonorRow
            key={member.id}
            palette={palette}
            title={donorDisplayName(member)}
            subtitle={`${fmtMoney(gap)} gap${suggestedHint}`}
            subtitleColor="#10b981"
            onClick={() => navigate("memberDetail", member)}
          />
        );
      })}
      {opportunity.length > TOP_N && (
        <DeepLinkRow
          palette={palette}
          count={opportunity.length}
          label="opportunities"
          onClick={() => navigate("capacity")}
        />
      )}
    </div>
  );
};

const SECTION_BODIES = {
  tasks: TasksSectionBody,
  overdue: OverdueSectionBody,
  opportunity: OpportunitySectionBody,
};

// ── Count selectors for the collapsed strip ──────────────────

const useSectionCount = (sectionId) => {
  const tasks = useMyTasks();
  const overdue = useMyOverdue();
  const opportunity = useMyOpportunity();
  if (sectionId === "tasks") return { kind: "n", value: tasks.length };
  if (sectionId === "overdue") return { kind: "n", value: overdue.length };
  if (sectionId === "opportunity") {
    const totalGap = opportunity.reduce((sum, r) => sum + (r.gap || 0), 0);
    return {
      kind: "money",
      value: totalGap,
      count: opportunity.length,
    };
  }
  return { kind: "n", value: 0 };
};

// ── Collapsed badge ──────────────────────────────────────────

const CollapsedBadge = ({ section, palette, onClick }) => {
  const Icon = section.icon;
  const stat = useSectionCount(section.id);
  const hasSignal =
    (stat.kind === "n" && stat.value > 0) ||
    (stat.kind === "money" && stat.count > 0);
  const label =
    stat.kind === "money"
      ? fmtMoney(stat.value)
      : String(stat.value);

  return (
    <button
      onClick={onClick}
      title={`${section.label} — click to expand`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        padding: "10px 4px",
        background: "none",
        border: "none",
        borderBottom: `1px solid ${palette.cardBorder}`,
        cursor: "pointer",
        width: "100%",
        fontFamily: FONT_FAMILY,
      }}
    >
      <Icon
        size={ICON_SIZES.sm}
        style={{ color: hasSignal ? section.color : palette.text3 }}
      />
      <span
        style={{
          fontSize: "0.65rem",
          fontWeight: 700,
          color: hasSignal ? section.color : palette.text3,
        }}
      >
        {label}
      </span>
    </button>
  );
};

// ── Gear menu (picker + reorder) ─────────────────────────────

const GearMenu = ({ palette, sections, onToggle, onMove, onClose }) => (
  <div
    style={{
      position: "absolute",
      top: "100%",
      right: 0,
      marginTop: 4,
      background: palette.cardBg,
      border: `1px solid ${palette.cardBorder}`,
      borderRadius: 8,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      zIndex: 50,
      minWidth: 240,
      overflow: "hidden",
    }}
    onMouseLeave={onClose}
  >
    <div
      style={{
        padding: "8px 12px",
        borderBottom: `1px solid ${palette.cardBorder}`,
        fontSize: "0.65rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: palette.text3,
        fontFamily: FONT_FAMILY,
      }}
    >
      My Day sections
    </div>
    {SECTION_REGISTRY.map((section) => {
      const enabled = sections.includes(section.id);
      const idx = sections.indexOf(section.id);
      const canMoveUp = enabled && idx > 0;
      const canMoveDown = enabled && idx >= 0 && idx < sections.length - 1;
      return (
        <div
          key={section.id}
          style={{
            padding: "8px 12px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: FONT_FAMILY,
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={() => onToggle(section.id)}
            style={{ cursor: "pointer" }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 600,
                color: palette.text1,
              }}
            >
              {section.label}
            </div>
            <div
              style={{
                fontSize: "0.65rem",
                color: palette.text3,
              }}
            >
              {section.description}
            </div>
          </div>
          <button
            onClick={() => onMove(section.id, -1)}
            disabled={!canMoveUp}
            title="Move up"
            style={{
              background: "none",
              border: "none",
              padding: 2,
              cursor: canMoveUp ? "pointer" : "default",
              opacity: canMoveUp ? 0.7 : 0.2,
              display: "flex",
            }}
          >
            <ArrowUp size={ICON_SIZES.xs} style={{ color: palette.text1 }} />
          </button>
          <button
            onClick={() => onMove(section.id, 1)}
            disabled={!canMoveDown}
            title="Move down"
            style={{
              background: "none",
              border: "none",
              padding: 2,
              cursor: canMoveDown ? "pointer" : "default",
              opacity: canMoveDown ? 0.7 : 0.2,
              display: "flex",
            }}
          >
            <ArrowDown size={ICON_SIZES.xs} style={{ color: palette.text1 }} />
          </button>
        </div>
      );
    })}
  </div>
);

// ── Section block (expanded) ─────────────────────────────────

const SectionBlock = ({ section, palette, navigate }) => {
  const Body = SECTION_BODIES[section.id];
  const Icon = section.icon;
  return (
    <div
      style={{
        marginBottom: 14,
        borderRadius: 8,
        overflow: "hidden",
        border: `1px solid ${section.color}33`,
        background: palette.cardBg,
      }}
    >
      {/* Colored header band */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: `${section.color}18`,
          borderLeft: `4px solid ${section.color}`,
          borderBottom: `1px solid ${section.color}22`,
        }}
      >
        <Icon size={ICON_SIZES.sm} style={{ color: section.color }} />
        <span
          style={{
            fontSize: FONT_SIZES.sm,
            fontWeight: 700,
            color: section.color,
            fontFamily: FONT_FAMILY,
            letterSpacing: "0.01em",
          }}
        >
          {section.label}
        </span>
      </div>
      {/* Body */}
      <div style={{ padding: "8px 12px" }}>
        <Body palette={palette} navigate={navigate} />
      </div>
    </div>
  );
};

// ── Main Sidebar ─────────────────────────────────────────────

export const MyDaySidebar = () => {
  const { palette, userProfile, currentUser, navigate } = useApp();

  const [localSections, setLocalSections] = useState(null);
  const [localCollapsed, setLocalCollapsed] = useState(null);
  const [showGear, setShowGear] = useState(false);

  useEffect(() => {
    const saved = userProfile?.my_day_sections;
    if (Array.isArray(saved)) {
      setLocalSections(saved.length > 0 ? saved : []);
    } else {
      setLocalSections(DEFAULT_SECTIONS);
    }
    if (typeof userProfile?.my_day_collapsed === "boolean") {
      setLocalCollapsed(userProfile.my_day_collapsed);
    } else {
      setLocalCollapsed(false);
    }
  }, [userProfile]);

  const sections = localSections || DEFAULT_SECTIONS;
  const collapsed = localCollapsed === true;

  const persistSections = useCallback(
    async (next) => {
      setLocalSections(next);
      if (!currentUser?.id) return;
      try {
        const { error } = await supabase
          .from("profiles")
          .update({ my_day_sections: next })
          .eq("id", currentUser.id);
        if (error) {
          debugLog.error("MyDaySidebar: save sections failed", error);
        } else if (userProfile) {
          userProfile.my_day_sections = next;
        }
      } catch (err) {
        debugLog.error("MyDaySidebar: save sections error", err);
      }
    },
    [currentUser, userProfile]
  );

  const persistCollapsed = useCallback(
    async (next) => {
      setLocalCollapsed(next);
      if (!currentUser?.id) return;
      try {
        const { error } = await supabase
          .from("profiles")
          .update({ my_day_collapsed: next })
          .eq("id", currentUser.id);
        if (error) {
          debugLog.error("MyDaySidebar: save collapsed failed", error);
        } else if (userProfile) {
          userProfile.my_day_collapsed = next;
        }
      } catch (err) {
        debugLog.error("MyDaySidebar: save collapsed error", err);
      }
    },
    [currentUser, userProfile]
  );

  const handleToggleSection = useCallback(
    (id) => {
      if (sections.includes(id)) {
        persistSections(sections.filter((s) => s !== id));
      } else {
        persistSections([...sections, id]);
      }
    },
    [sections, persistSections]
  );

  const handleMoveSection = useCallback(
    (id, direction) => {
      const idx = sections.indexOf(id);
      if (idx < 0) return;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= sections.length) return;
      const next = [...sections];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      persistSections(next);
    },
    [sections, persistSections]
  );

  // Empty state: no sections enabled
  if (sections.length === 0 && !collapsed) {
    return (
      <aside
        style={{
          width: EXPANDED_WIDTH,
          flexShrink: 0,
          background: palette.cardBg,
          borderRight: `1px solid ${palette.cardBorder}`,
          padding: "14px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          fontFamily: FONT_FAMILY,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: palette.text1,
            }}
          >
            My Day
          </span>
          <button
            onClick={() => persistCollapsed(true)}
            title="Collapse"
            style={{
              background: "none",
              border: "none",
              padding: 4,
              cursor: "pointer",
              display: "flex",
            }}
          >
            <ChevronLeft size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
          </button>
        </div>
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text3,
            fontStyle: "italic",
          }}
        >
          All sections hidden.
        </div>
        <button
          onClick={() => persistSections(DEFAULT_SECTIONS)}
          style={{
            background: "none",
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: FONT_SIZES.xs,
            fontWeight: 600,
            color: palette.text1,
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
            alignSelf: "flex-start",
          }}
        >
          Restore defaults
        </button>
      </aside>
    );
  }

  // Collapsed state: 44px strip with count badges
  if (collapsed) {
    return (
      <aside
        style={{
          width: COLLAPSED_WIDTH,
          flexShrink: 0,
          background: palette.cardBg,
          borderRight: `1px solid ${palette.cardBorder}`,
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
        }}
      >
        <button
          onClick={() => persistCollapsed(false)}
          title="Expand My Day"
          style={{
            background: "none",
            border: "none",
            borderBottom: `1px solid ${palette.cardBorder}`,
            padding: "10px 4px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ChevronRight size={ICON_SIZES.sm} style={{ color: palette.text2 }} />
        </button>
        {sections.map((id) => {
          const section = getSection(id);
          if (!section) return null;
          return (
            <CollapsedBadge
              key={id}
              section={section}
              palette={palette}
              onClick={() => persistCollapsed(false)}
            />
          );
        })}
      </aside>
    );
  }

  // Expanded state
  return (
    <aside
      style={{
        width: EXPANDED_WIDTH,
        flexShrink: 0,
        background: palette.cardBg,
        borderRight: `1px solid ${palette.cardBorder}`,
        display: "flex",
        flexDirection: "column",
        fontFamily: FONT_FAMILY,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: `1px solid ${palette.cardBorder}`,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZES.md,
            fontWeight: 700,
            color: palette.text1,
          }}
        >
          My Day
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowGear((prev) => !prev)}
              title="Configure sections"
              style={{
                background: showGear ? `${palette.accent1}12` : "none",
                border: "none",
                padding: 4,
                cursor: "pointer",
                display: "flex",
                borderRadius: 4,
              }}
            >
              <Gear size={ICON_SIZES.sm} style={{ color: palette.text2 }} />
            </button>
            {showGear && (
              <GearMenu
                palette={palette}
                sections={sections}
                onToggle={handleToggleSection}
                onMove={handleMoveSection}
                onClose={() => setShowGear(false)}
              />
            )}
          </div>
          <button
            onClick={() => persistCollapsed(true)}
            title="Collapse"
            style={{
              background: "none",
              border: "none",
              padding: 4,
              cursor: "pointer",
              display: "flex",
            }}
          >
            <ChevronLeft
              size={ICON_SIZES.sm}
              style={{ color: palette.text3 }}
            />
          </button>
        </div>
      </div>

      {/* Section body — scrollable if content exceeds viewport */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "12px 16px",
        }}
      >
        {sections.map((id) => {
          const section = getSection(id);
          if (!section) return null;
          return (
            <SectionBlock
              key={id}
              section={section}
              palette={palette}
              navigate={navigate}
            />
          );
        })}
      </div>
    </aside>
  );
};
